import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { client } from "./api-client";
import {
  type Admin,
  type AdminListResponse,
  type AdminStats,
  type CreateAdminRequest,
  type AdminListParams,
  type UserListResponse,
  type UserListParams,
  type UserManagementStats,
  type UserListItem,
  type UserWalletListResponse,
  type UserWalletBalanceResponse,
} from "utils/types/user.type";

// Get current admin profile
export const useGetCurrentAdmin = () =>
  useQuery<Admin, Error>(
    ["getCurrentAdmin"],
    async () => await client("admin/me").then((res) => res.data)
  );


// List admins (Super Admin only)
export const useGetAdminList = (params?: AdminListParams) =>
  useQuery<AdminListResponse, Error>(
    ["getAdminList", params],
    async () => {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.limit) queryParams.append("limit", params.limit.toString());
      if (params?.search_query)
        queryParams.append("search_query", params.search_query);
      if (params?.status) queryParams.append("status", params.status);

      const queryString = queryParams.toString();
      return await client(
        `admin/list${queryString ? `?${queryString}` : ""}`
      ).then((res) => res.data);
    }
  );

// Create admin
export const useCreateAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation<Admin, Error, CreateAdminRequest>(
    async (data) =>
      await client("admin/create", {
        data,
      }).then((res) => res.data),
    {
      onSuccess() {
        void queryClient.invalidateQueries(["getAdminList"]);
      },
    }
  );
};


// Delete admin
export const useDeleteAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, string>(
    async (id) =>
      await client(`admin/${id}`, {
        method: "DELETE",
      }).then((res) => res.data),
    {
      onSuccess() {
        void queryClient.invalidateQueries(["getAdminList"]);
      },
    }
  );
};

// Toggle admin status
export const useToggleAdminStatus = () => {
  const queryClient = useQueryClient();
  return useMutation<Admin, Error, string>(
    async (id) =>
      await client(`admin/toggle-status/${id}`, {
        method: "PATCH",
      }).then((res) => res.data),
    {
      onSuccess() {
        void queryClient.invalidateQueries(["getAdminList"]);
      },
    }
  );
};


// Reset admin password (Super Admin Only)
export const useResetAdminPassword = () => {
  const queryClient = useQueryClient();
  return useMutation<
    Admin,
    Error,
    { id: string; password: string }
  >(
    async ({ id, password }) =>
      await client(`admin/reset-password/${id}`, {
        method: "PATCH",
        data: { password },
      }).then((res) => res.data),
    {
      onSuccess() {
        void queryClient.invalidateQueries(["getAdminList"]);
      },
    }
  );
};

// Get admin statistics
export const useGetAdminStats = () =>
  useQuery<AdminStats, Error>(
    ["getAdminStats"],
    async () => await client("admin/stats").then((res) => res.data)
  );

// User Management Endpoints

// List all users (Admin only)
export const useGetAllUsers = (params?: UserListParams) =>
  useQuery<UserListResponse, Error>(
    ["getAllUsers", params],
    async () => {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.limit) queryParams.append("limit", params.limit.toString());
      if (params?.search_query)
        queryParams.append("search_query", params.search_query);
      if (params?.status) queryParams.append("status", params.status);

      const queryString = queryParams.toString();
      return await client(
        `admin/get-all-users${queryString ? `?${queryString}` : ""}`
      ).then((res) => res.data);
    }
  );

// Toggle user status
export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();
  return useMutation<UserListItem, Error, string>(
    async (id) =>
      await client(`admin/toggle-user-status/${id}`, {
        method: "PATCH",
      }).then((res) => res.data),
    {
      onSuccess() {
        void queryClient.invalidateQueries(["getAllUsers"]);
        void queryClient.invalidateQueries(["getUserStats"]);
      },
    }
  );
};

// Get user statistics
export const useGetUserStats = () =>
  useQuery<UserManagementStats, Error>(
    ["getUserStats"],
    async () => await client("admin/get-user-stats").then((res) => res.data)
  );

// Get user wallet list
export const useGetUserWalletList = (userId: string | null) =>
  useQuery<UserWalletListResponse, Error>(
    ["getUserWalletList", userId],
    async () => {
      if (!userId) throw new Error("User ID is required");
      return await client(`admin/get-user-wallet-list/${userId}`).then((res) => res);
    },
    {
      enabled: !!userId,
    }
  );

// Get user wallet balance
export const useGetUserWalletBalance = (
  userId: string | null,
  userWalletId: string | null
) =>
  useQuery<UserWalletBalanceResponse, Error>(
    ["getUserWalletBalance", userId, userWalletId],
    async () => {
      if (!userId || !userWalletId) throw new Error("User ID and Wallet ID are required");
      return await client(
        `admin/get-user-wallet-balance/${userId}/${userWalletId}`
      ).then((res) => res);
    },
    {
      enabled: !!userId && !!userWalletId,
    }
  );

